import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import Draggable from 'react-draggable';
import { MdClose, MdColorLens, MdFullscreenExit, MdReplay, MdPlayArrow, MdStop } from 'react-icons/md';
import { TbArrowsMinimize, TbArrowsMaximize } from 'react-icons/tb';
import { FiRefreshCw, FiChevronRight } from 'react-icons/fi';
import { BsExclamationLg } from 'react-icons/bs';
import { Menu, Item, useContextMenu } from 'react-contexify';
import 'react-contexify/ReactContexify.css';
import dropPinIconUrl from 'assets/images/survey-drop-pin-icon.png';
import dropPinManIconUrl from 'assets/images/survey-drop-pin-man-icon.png';
import { SurveyLayerEntry } from './types';
import {
  DROP_PIN_CURSOR_SIZE_PX,
  DROP_PIN_CURSOR_HOTSPOT_Y_PX,
  buildDropPinCursorTransform,
  resolveDropPinDragEndAction,
  resolveDropPinIconClassName,
  resolveDropPinReturnDurationMs,
  resolveDropPinReturnEndScale,
  type DropPinCursorPhase,
  type DropPinLandingApi,
} from './dropPinCursor';
import { SURVEY_IMAGE_DRAG_MIME } from '../../containers/Dashboard/surveyImageDrag';
import { SymbologyModelRecord } from '../../models/SymbologyModel';
import Symbology from '../../containers/SurveyManager/symbology';
import renderMarkerType from '../../containers/SurveyManager/makerTypesRenderer';
import type { RgbaColor } from 'react-colorful';
import { useDraggableModalPosition } from 'hooks/useDraggableModalPosition';
import './style.scss';

export type { DropPinLandingApi } from './dropPinCursor';

const MENU_ID = 'survey-layer-panel-menu';
/** 1×1 transparent GIF — Chrome draws a dashed box if setDragImage uses an empty canvas. */
const TRANSPARENT_PIXEL_GIF =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface Props {
  isOpen: boolean;
  surveys: SurveyLayerEntry[];
  onClose: () => void;
  onToggleLayer: (surveySlug: string, layerKey: string, enabled: boolean) => void;
  onRemoveSurvey: (surveySlug: string) => void;
  onSelectLayer: (layerKey: string) => void;
  onHighlightSurvey: (surveySlug: string) => void;
  onHighlightSurveyOnMapOnly?: (surveySlug: string) => void;
  onSurveyImageDragStart?: (surveySlug: string) => void;
  onSurveyImageDragEnd?: () => boolean;
  onSurveyImageDragCancel?: () => void;
  dropPinLandingApiRef?: React.MutableRefObject<DropPinLandingApi | null>;
  onZoomToExtent: (layerKey: string) => void;
  onGoToStart: (layerKey: string) => void;
  setLayerStyle: (key: string, color: RgbaColor, width: number, size: number, lineStyle: string, markerStyle: string, angle: number) => void;
  styles: SymbologyModelRecord;
  highlightedSurveySlug?: string | null;
  zIndex?: number;
  activeSurveyPlaybackSlug?: string | null;
  playbackLoadingSurveySlug?: string | null;
  isPlaybackGridOpen?: boolean;
  onPlaySurvey?: (surveySlug: string) => void;
}

const SurveyLayerPanel: React.FC<Props> = ({
  isOpen,
  surveys,
  onClose,
  onToggleLayer,
  onRemoveSurvey,
  onSelectLayer,
  onHighlightSurvey,
  onHighlightSurveyOnMapOnly,
  onSurveyImageDragStart,
  onSurveyImageDragEnd,
  onSurveyImageDragCancel,
  dropPinLandingApiRef,
  onZoomToExtent,
  onGoToStart,
  setLayerStyle,
  styles,
  highlightedSurveySlug,
  zIndex,
  activeSurveyPlaybackSlug,
  playbackLoadingSurveySlug = null,
  isPlaybackGridOpen = false,
  onPlaySurvey,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [draggingSurveySlug, setDraggingSurveySlug] = useState<string | null>(null);
  const [dropPinButtonMountKey, setDropPinButtonMountKey] = useState(0);
  const blankDragImageRef = useRef<HTMLImageElement | null>(null);
  const dropPinCursorElRef = useRef<HTMLDivElement | null>(null);
  const dropPinCursorIconRef = useRef<HTMLImageElement | null>(null);
  const dropPinSourceElRef = useRef<HTMLElement | null>(null);
  const dropPinPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dropPinRafRef = useRef<number | null>(null);
  const dropPinReturnAnimRef = useRef<Animation | null>(null);
  const isDropPinDraggingRef = useRef(false);
  const dropPinCursorPhaseRef = useRef<DropPinCursorPhase | null>(null);
  const onSurveyImageDragStartRef = useRef(onSurveyImageDragStart);
  onSurveyImageDragStartRef.current = onSurveyImageDragStart;
  const onSurveyImageDragEndRef = useRef(onSurveyImageDragEnd);
  onSurveyImageDragEndRef.current = onSurveyImageDragEnd;
  const onSurveyImageDragCancelRef = useRef(onSurveyImageDragCancel);
  onSurveyImageDragCancelRef.current = onSurveyImageDragCancel;
  const [collapsedSlugs, setCollapsedSlugs] = useState<Record<string, boolean>>({});
  const previousSurveySlugsRef = useRef<Set<string>>(new Set());
  const [topLayerKeysBySurvey, setTopLayerKeysBySurvey] = useState<Record<string, string | null>>({});

  // Default newly added survey rows to collapsed, and drop state for removed rows.
  useEffect(() => {
    const currentSlugs = new Set(surveys.map((survey) => survey.surveySlug));
    setCollapsedSlugs((prev) => {
      const next: Record<string, boolean> = {};
      for (const survey of surveys) {
        const isNewSurvey = !previousSurveySlugsRef.current.has(survey.surveySlug);
        next[survey.surveySlug] = isNewSurvey ? true : !!prev[survey.surveySlug];
      }
      return next;
    });
    setTopLayerKeysBySurvey((prev) => {
      const next: Record<string, string | null> = {};
      for (const survey of surveys) {
        next[survey.surveySlug] = previousSurveySlugsRef.current.has(survey.surveySlug)
          ? prev[survey.surveySlug] ?? null
          : null;
      }
      return next;
    });
    previousSurveySlugsRef.current = currentSlugs;
  }, [surveys]);

  useEffect(() => {
    if (isOpen) {
      setTopLayerKeysBySurvey({});
    }
  }, [isOpen]);

  const applyDropPinCursorPosition = useCallback((x: number, y: number) => {
    dropPinPositionRef.current.x = x;
    dropPinPositionRef.current.y = y;
    const el = dropPinCursorElRef.current;
    if (!el) {
      return;
    }
    el.style.transform = buildDropPinCursorTransform(x, y);
  }, []);

  const setDropPinCursorPhase = useCallback((phase: DropPinCursorPhase | null) => {
    dropPinCursorPhaseRef.current = phase;
    const root = dropPinCursorElRef.current;
    const icon = dropPinCursorIconRef.current;
    // Imperative DOM only — avoid React re-renders on every phase change during drag.
    if (root && icon) {
      if (!phase) {
        root.classList.remove(
          'slp-drop-pin-drag-cursor--visible',
          'slp-drop-pin-drag-cursor--dragging',
          'slp-drop-pin-drag-cursor--awaiting-land',
        );
        root.style.opacity = '0';
        root.style.visibility = 'hidden';
      } else {
        root.style.opacity = '';
        root.style.visibility = '';
        root.classList.add('slp-drop-pin-drag-cursor--visible');
        root.classList.toggle(
          'slp-drop-pin-drag-cursor--dragging',
          phase === 'dragging',
        );
        root.classList.toggle(
          'slp-drop-pin-drag-cursor--awaiting-land',
          phase === 'awaiting-land',
        );
      }
      icon.className = resolveDropPinIconClassName(phase);
    }
  }, []);

  const scheduleDropPinCursorPosition = useCallback(
    (x: number, y: number) => {
      // Some browsers report 0,0 on drag events outside the window.
      if (x === 0 && y === 0) {
        return;
      }
      // Skip sub-pixel / identical samples to cut style thrash (Firefox).
      if (
        Math.abs(x - dropPinPositionRef.current.x) < 0.5 &&
        Math.abs(y - dropPinPositionRef.current.y) < 0.5
      ) {
        return;
      }
      dropPinPositionRef.current.x = x;
      dropPinPositionRef.current.y = y;
      if (dropPinRafRef.current != null) {
        return;
      }
      dropPinRafRef.current = window.requestAnimationFrame(() => {
        dropPinRafRef.current = null;
        applyDropPinCursorPosition(
          dropPinPositionRef.current.x,
          dropPinPositionRef.current.y,
        );
      });
    },
    [applyDropPinCursorPosition],
  );

  const clearDropPinCursor = useCallback(() => {
    if (dropPinRafRef.current != null) {
      window.cancelAnimationFrame(dropPinRafRef.current);
      dropPinRafRef.current = null;
    }
    const returnAnim = dropPinReturnAnimRef.current;
    dropPinReturnAnimRef.current = null;
    if (returnAnim) {
      try {
        returnAnim.cancel();
      } catch {
        // Animation may already be finished.
      }
    }
    const el = dropPinCursorElRef.current;
    if (el) {
      // Hide before cancelling WAAPI — cancel can flash the pre-animation pose.
      el.style.opacity = '0';
      el.style.visibility = 'hidden';
      if (typeof el.getAnimations === 'function') {
        for (const animation of el.getAnimations()) {
          try {
            animation.cancel();
          } catch {
            // Ignore already-finished animations.
          }
        }
      }
      el.style.transform = 'translate3d(-9999px, -9999px, 0)';
    }
    isDropPinDraggingRef.current = false;
    dropPinSourceElRef.current = null;
    delete document.documentElement.dataset.xaSurveyDropAllowed;
    setDropPinCursorPhase(null);
  }, [setDropPinCursorPhase]);

  const beginDropPinAwaitLanding = useCallback(() => {
    isDropPinDraggingRef.current = false;
    if (dropPinRafRef.current != null) {
      window.cancelAnimationFrame(dropPinRafRef.current);
      dropPinRafRef.current = null;
    }
    const phase = dropPinCursorPhaseRef.current;
    // dragend can fire after Dashboard already took over the map fall.
    if (phase === 'awaiting-land') {
      return;
    }
    if (phase !== 'dragging') {
      return;
    }
    setDropPinCursorPhase('awaiting-land');
  }, [setDropPinCursorPhase]);

  const abortDropPinLanding = useCallback(() => {
    if (dropPinReturnAnimRef.current) {
      dropPinReturnAnimRef.current.cancel();
      dropPinReturnAnimRef.current = null;
    }
    clearDropPinCursor();
    setDraggingSurveySlug(null);
  }, [clearDropPinCursor]);

  const suppressDropPinForMapFall = useCallback(() => {
    if (dropPinReturnAnimRef.current) {
      dropPinReturnAnimRef.current.cancel();
      dropPinReturnAnimRef.current = null;
    }
    clearDropPinCursor();
  }, [clearDropPinCursor]);

  const releaseDropPinSource = useCallback(() => {
    // Clear the man placeholder and mount a fresh pin icon in place (no fly-back).
    setDraggingSurveySlug(null);
    setDropPinButtonMountKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!dropPinLandingApiRef) {
      return;
    }
    dropPinLandingApiRef.current = {
      abortLanding: abortDropPinLanding,
      suppressForMapFall: suppressDropPinForMapFall,
      releaseSource: releaseDropPinSource,
    };
    return () => {
      dropPinLandingApiRef.current = null;
    };
  }, [
    abortDropPinLanding,
    dropPinLandingApiRef,
    releaseDropPinSource,
    suppressDropPinForMapFall,
  ]);

  /** Missed drop — animate the ghost back to the button, then restore the pin icon. */
  const beginDropPinReturnHome = useCallback(() => {
    isDropPinDraggingRef.current = false;
    if (dropPinRafRef.current != null) {
      window.cancelAnimationFrame(dropPinRafRef.current);
      dropPinRafRef.current = null;
    }
    if (dropPinReturnAnimRef.current) {
      dropPinReturnAnimRef.current.cancel();
      dropPinReturnAnimRef.current = null;
    }

    const el = dropPinCursorElRef.current;
    const icon = dropPinCursorIconRef.current;
    const source = dropPinSourceElRef.current;
    if (!el || !source || dropPinCursorPhaseRef.current !== 'dragging') {
      clearDropPinCursor();
      setDraggingSurveySlug(null);
      onSurveyImageDragCancelRef.current?.();
      return;
    }

    // Keep the floating ghost visible while it flies home.
    el.style.opacity = '';
    el.style.visibility = '';
    el.classList.add('slp-drop-pin-drag-cursor--visible');
    // Keep React phase as "dragging" so the source stays as the man placeholder.
    dropPinCursorPhaseRef.current = 'returning';
    if (icon) {
      icon.className = resolveDropPinIconClassName('returning');
    }

    const target =
      (source.querySelector('.slp-drop-pin-icon') as HTMLElement | null) ?? source;
    const rect = target.getBoundingClientRect();
    const half = DROP_PIN_CURSOR_SIZE_PX / 2;
    const endScale = resolveDropPinReturnEndScale(rect.width);
    const fromX = dropPinPositionRef.current.x - half;
    const fromY = dropPinPositionRef.current.y - DROP_PIN_CURSOR_HOTSPOT_Y_PX;
    const toX = rect.left + rect.width / 2 - half;
    const toY = rect.top + rect.height / 2 - half;
    const distance = Math.hypot(toX - fromX, toY - fromY);
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = resolveDropPinReturnDurationMs(distance, prefersReducedMotion);
    const startTransform = `${buildDropPinCursorTransform(
      dropPinPositionRef.current.x,
      dropPinPositionRef.current.y,
    )} scale(1)`;
    const endTransform = `translate3d(${toX}px, ${toY}px, 0) scale(${endScale})`;
    el.style.transform = startTransform;

    const finishReturn = () => {
      const finishedAnim = dropPinReturnAnimRef.current;
      dropPinReturnAnimRef.current = null;
      if (finishedAnim) {
        try {
          finishedAnim.cancel();
        } catch {
          // Already finished/cancelled.
        }
      }
      clearDropPinCursor();
      window.requestAnimationFrame(() => {
        setDraggingSurveySlug(null);
        onSurveyImageDragCancelRef.current?.();
      });
    };

    if (prefersReducedMotion || typeof el.animate !== 'function') {
      el.style.transform = endTransform;
      finishReturn();
      return;
    }

    const animation = el.animate(
      [{ transform: startTransform }, { transform: endTransform }],
      {
        duration,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'forwards',
        composite: 'replace',
      },
    );
    dropPinReturnAnimRef.current = animation;
    animation.onfinish = finishReturn;
  }, [clearDropPinCursor]);

  // Persistent document listeners for cursor tracking only.
  // Drop permission is owned by the map (highlighted survey layer) — never claim move here.
  useEffect(() => {
    const onDragOver = (event: DragEvent) => {
      if (!isDropPinDraggingRef.current) {
        return;
      }
      scheduleDropPinCursorPosition(event.clientX, event.clientY);
    };

    document.addEventListener('dragover', onDragOver, {
      passive: true,
      capture: true,
    });

    return () => {
      document.removeEventListener('dragover', onDragOver, true);
      if (dropPinRafRef.current != null) {
        window.cancelAnimationFrame(dropPinRafRef.current);
        dropPinRafRef.current = null;
      }
      if (dropPinReturnAnimRef.current) {
        dropPinReturnAnimRef.current.cancel();
        dropPinReturnAnimRef.current = null;
      }
    };
  }, [scheduleDropPinCursorPosition]);

  const [symbologyOpen, setSymbologyOpen] = useState(false);
  const [symbologyData, setSymbologyData] = useState<{ key: string; name: string }>({ key: '', name: '' });

  const { show } = useContextMenu({ id: MENU_ID });

  const isWfsProps = (props: any): boolean => {
    const urlLower = (props?.endpoint || '').toLowerCase();
    return (
      props?.type === 'WFS' ||
      urlLower.includes('service=wfs') ||
      urlLower.includes('request=getfeature')
    );
  };

  const parseColor = (value?: string): RgbaColor | undefined => {
    if (!value) return undefined;
    const color = value.trim();

    if (color.startsWith('#')) {
      const hex = color.slice(1);
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16),
          a: 1,
        };
      }
      if (hex.length === 6) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
          a: 1,
        };
      }
    }

    const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbaMatch) {
      const parts = rgbaMatch[1].split(',').map((p) => p.trim());
      if (parts.length >= 3) {
        const r = Number(parts[0]);
        const g = Number(parts[1]);
        const b = Number(parts[2]);
        const a = parts.length >= 4 ? Number(parts[3]) : 1;
        if ([r, g, b, a].every((n) => Number.isFinite(n))) {
          return { r, g, b, a };
        }
      }
    }

    return undefined;
  };

  const resolveRgbaColor = (value: unknown): RgbaColor | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return parseColor(value);
    if (typeof value === 'object') {
      const c = value as any;
      if ([c.r, c.g, c.b].every((n: unknown) => typeof n === 'number')) {
        return { r: c.r, g: c.g, b: c.b, a: typeof c.a === 'number' ? c.a : 1 };
      }
    }
    return undefined;
  };

  const handleZoomToExtent = (ev: any) => onZoomToExtent(ev.props.key);
  const handleGoToStart = (ev: any) => onGoToStart(ev.props.key);
  const handleOpenSymbology = (ev: any) => {
    setSymbologyData({ key: ev.props.key, name: ev.props.name });
    setSymbologyOpen(true);
  };


  const { position, bounds, modalRef, onDragStop } = useDraggableModalPosition({
    initialOffset: { x: 0, y: 0 },
  });

  const setCollapsed = (slug: string, collapsed: boolean) => {
    setCollapsedSlugs((prev) => ({ ...prev, [slug]: collapsed }));
  };

  /** Label expands (if needed) and selects on the map; it never collapses. */
  const handleSurveyLabelClick = (surveySlug: string) => {
    if (collapsedSlugs[surveySlug]) {
      setCollapsed(surveySlug, false);
    }
    onHighlightSurvey(surveySlug);
  };

  /** Chevron toggles expand/collapse and selects on the map. */
  const handleSurveyChevronClick = (surveySlug: string) => {
    setCollapsed(surveySlug, !collapsedSlugs[surveySlug]);
    onHighlightSurvey(surveySlug);
  };

  const getTopLayerKey = (surveySlug: string) => topLayerKeysBySurvey[surveySlug] ?? null;

  const setSurveyTopLayerKey = (surveySlug: string, layerKey: string | null) => {
    setTopLayerKeysBySurvey((prev) => ({
      ...prev,
      [surveySlug]: layerKey,
    }));
  };

  return (
    <>
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      bodyOpenClassName={null}
      portalClassName="react-modal-container"
      overlayClassName="react-modal-overlay"
      className="react-modal-wrapper"
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <Draggable
        nodeRef={modalRef}
        enableUserSelectHack={false}
        handle=".modal-header"
        position={position}
        onStop={onDragStop}
        bounds={bounds}
      >
        <div
          ref={modalRef}
          className={`react-modal top-left slp-panel${isMinimized ? ' minimized' : ''}`}
          style={{ maxWidth: 'calc(100vw - 150.2rem)', ...(zIndex != null ? { zIndex } : {}) }}
        >
          <div className="modal-header">
            <div className="modal-title-container">
              <div className="modal-title-in-container">Survey Layers</div>
            </div>
            <div className="modal-header-buttons">
              <div className="modal-control" onClick={() => setIsMinimized((p) => !p)}>
                {isMinimized ? <TbArrowsMaximize /> : <TbArrowsMinimize />}
              </div>
              <div className="modal-close" onClick={onClose}>
                <MdClose />
              </div>
            </div>
          </div>

          <div className="modal-content slp-content">
            {surveys.length === 0 && (
              <p className="slp-empty">No surveys added yet.</p>
            )}
            {surveys.map((entry) => {
              const isCollapsed = !!collapsedSlugs[entry.surveySlug];
              const topLayerKey = getTopLayerKey(entry.surveySlug);
              return (
                <div key={entry.surveySlug} className="slp-survey-node">
                  {/* Top-level survey row */}
                  <div
                    className={`slp-survey-header${highlightedSurveySlug === entry.surveySlug ? ' slp-survey-header--highlighted' : ''}`}
                  >
                    <button
                      className="slp-collapse-btn"
                      onClick={() => handleSurveyChevronClick(entry.surveySlug)}
                      aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                      aria-expanded={!isCollapsed}
                    >
                      <FiChevronRight
                        className={`slp-collapse-chevron${isCollapsed ? '' : ' slp-collapse-chevron--open'}`}
                      />
                    </button>
                    <span
                      className="slp-survey-label slp-survey-label--clickable"
                      title={isCollapsed ? 'Click to expand' : 'Click to select on map'}
                      onClick={() => {
                        handleSurveyLabelClick(entry.surveySlug);
                      }}
                    >
                      {entry.displayLabel}
                    </span>
                    <button
                      className={`slp-play-btn${activeSurveyPlaybackSlug === entry.surveySlug ? ' slp-play-btn--active' : ''}${playbackLoadingSurveySlug === entry.surveySlug ? ' slp-play-btn--loading' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlaySurvey?.(entry.surveySlug);
                      }}
                      title={
                        playbackLoadingSurveySlug === entry.surveySlug
                          ? 'Starting playback…'
                          : activeSurveyPlaybackSlug === entry.surveySlug
                            ? 'Stop playback'
                            : 'Start playback'
                      }
                      aria-label={
                        playbackLoadingSurveySlug === entry.surveySlug
                          ? 'Starting playback'
                          : activeSurveyPlaybackSlug === entry.surveySlug
                            ? 'Stop playback'
                            : 'Start playback'
                      }
                      disabled={
                        entry.loading ||
                        !!entry.error ||
                        (playbackLoadingSurveySlug != null &&
                          playbackLoadingSurveySlug !== entry.surveySlug)
                      }
                    >
                      {playbackLoadingSurveySlug === entry.surveySlug ? (
                        <FiRefreshCw className="slp-loading-icon rotating" aria-hidden />
                      ) : activeSurveyPlaybackSlug === entry.surveySlug ? (
                        <MdStop />
                      ) : (
                        <MdPlayArrow />
                      )}
                    </button>
                    <button
                      className={`slp-drop-pin-btn${draggingSurveySlug === entry.surveySlug ? ' slp-drop-pin-btn--dragging' : ''}${playbackLoadingSurveySlug === entry.surveySlug ? ' slp-drop-pin-btn--loading' : ''}`}
                      draggable={
                        !entry.loading &&
                        !entry.error &&
                        playbackLoadingSurveySlug !== entry.surveySlug
                      }
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        onHighlightSurveyOnMapOnly?.(entry.surveySlug);
                      }}
                      onDragStart={(e) => {
                        if (playbackLoadingSurveySlug === entry.surveySlug) {
                          e.preventDefault();
                          return;
                        }
                        e.dataTransfer.setData(SURVEY_IMAGE_DRAG_MIME, entry.surveySlug);
                        e.dataTransfer.effectAllowed = 'move';
                        const blank = blankDragImageRef.current;
                        if (blank) {
                          e.dataTransfer.setDragImage(blank, 0, 0);
                        }
                        if (dropPinReturnAnimRef.current) {
                          dropPinReturnAnimRef.current.cancel();
                          dropPinReturnAnimRef.current = null;
                          onSurveyImageDragCancelRef.current?.();
                        }
                        dropPinSourceElRef.current = e.currentTarget;
                        // Sync show + position before React paint so dragstart→dragover is seamless.
                        isDropPinDraggingRef.current = true;
                        const cursorEl = dropPinCursorElRef.current;
                        if (cursorEl && typeof cursorEl.getAnimations === 'function') {
                          for (const animation of cursorEl.getAnimations()) {
                            try {
                              animation.cancel();
                            } catch {
                              // Ignore already-finished animations.
                            }
                          }
                        }
                        applyDropPinCursorPosition(e.clientX, e.clientY);
                        document.documentElement.dataset.xaSurveyDropAllowed = '0';
                        setDropPinCursorPhase('dragging');
                        setDraggingSurveySlug(entry.surveySlug);
                        onSurveyImageDragStartRef.current?.(entry.surveySlug);
                      }}
                      onDragEnd={() => {
                        const dropped = onSurveyImageDragEndRef.current?.() === true;
                        const action = resolveDropPinDragEndAction(dropped);
                        if (action === 'land') {
                          beginDropPinAwaitLanding();
                          return;
                        }
                        // Outside droppable area — animate back to the button.
                        beginDropPinReturnHome();
                      }}
                      title={
                        playbackLoadingSurveySlug === entry.surveySlug
                          ? 'Opening images…'
                          : 'View Images Here: drag this onto the map'
                      }
                      aria-label={
                        playbackLoadingSurveySlug === entry.surveySlug
                          ? 'Opening images'
                          : 'View Images Here'
                      }
                      disabled={
                        entry.loading ||
                        !!entry.error ||
                        playbackLoadingSurveySlug === entry.surveySlug
                      }
                    >
                      {draggingSurveySlug === entry.surveySlug ? (
                        <img
                          src={dropPinManIconUrl}
                          alt=""
                          className="slp-drop-pin-icon slp-drop-pin-icon--man"
                          draggable={false}
                        />
                      ) : playbackLoadingSurveySlug === entry.surveySlug ? (
                        <FiRefreshCw
                          className="slp-loading-icon rotating"
                          aria-hidden
                        />
                      ) : (
                        <img
                          key={`slp-drop-pin-${entry.surveySlug}-${dropPinButtonMountKey}`}
                          src={dropPinIconUrl}
                          alt=""
                          className="slp-drop-pin-icon"
                          draggable={false}
                        />
                      )}
                    </button>
                    {entry.loading && (
                      <FiRefreshCw className="slp-loading-icon rotating" aria-label="Loading" />
                    )}
                    {entry.error && !entry.loading && (
                      <span className="slp-error-icon" title={entry.error} aria-label={entry.error}>
                        <BsExclamationLg color="red" size={16} aria-hidden />
                      </span>
                    )}
                    <button
                      className="slp-remove-btn"
                      onClick={() => {
                        onRemoveSurvey(entry.surveySlug);
                      }}
                      aria-label={`Remove ${entry.displayLabel}`}
                    >
                      <MdClose />
                    </button>
                  </div>

                  {/* Layer checkboxes */}
                  {!entry.loading && (
                    <div
                      className={`slp-layer-list-wrap${isCollapsed ? '' : ' slp-layer-list-wrap--open'}`}
                      aria-hidden={isCollapsed}
                    >
                      <div className="slp-layer-list">
                      {entry.layers.length === 0 && (
                        <span className="slp-no-layers" role="status">
                          <BsExclamationLg color="red" size={16} aria-hidden />
                          No data available
                        </span>
                      )}
                      {entry.layers.map((layer) => (
                        (() => {
                          const layerStyle = styles[layer.key];
                          const markerStyle = layerStyle?.currentMarkerStyle;
                          const markerColor =
                            parseColor(layer.color) ??
                            resolveRgbaColor(layerStyle?.currentColor) ??
                            { r: 48, g: 54, b: 150, a: 1 };
                          const markerType = markerStyle
                            ? markerStyle.startsWith('symbols-sls-')
                              ? markerStyle
                              : `symbols-sls-${markerStyle}`
                            : 'symbols-sls-circle';
                          const showMarkerIcon = layer.checked && layerStyle?.type === 'simple-marker';
                          const urlLower = (layer.endpoint || '').toLowerCase();
                          const isWmtsLayer =
                            (layer.type || '').toUpperCase() === 'WMTS' ||
                            urlLower.includes('service=wmts') ||
                            urlLower.includes('wmts');
                          const isWfsLayer =
                            layer.type === 'WFS' ||
                            urlLower.includes('service=wfs') ||
                            urlLower.includes('request=getfeature');
                          const isLayerLoading = !!layer.loading;
                          const isWfsSelectionLocked = isPlaybackGridOpen && isWfsLayer;
                          const isRowDisabled = isWfsSelectionLocked;

                          return (
                        <div
                          key={layer.key}
                          className={`slp-layer-row${isRowDisabled ? ' slp-layer-row--disabled' : ''}`}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (isRowDisabled || isLayerLoading) return;
                            if (!layer.checked) {
                              onToggleLayer(entry.surveySlug, layer.key, true);
                              onSelectLayer(layer.key);
                              setSurveyTopLayerKey(entry.surveySlug, layer.key);
                              return;
                            }
                            if (layer.key !== topLayerKey) {
                              onSelectLayer(layer.key);
                              setSurveyTopLayerKey(entry.surveySlug, layer.key);
                            }
                            show({ event: e, props: { key: layer.key, name: layer.name, type: layer.type, endpoint: layer.endpoint } });
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={layer.checked}
                            disabled={isRowDisabled}
                            onChange={(e) => {
                              if (isRowDisabled) return;
                              if (!e.target.checked && topLayerKey === layer.key) {
                                  setSurveyTopLayerKey(entry.surveySlug, null);
                              }
                              onToggleLayer(entry.surveySlug, layer.key, e.target.checked);
                              if (e.target.checked) {
                                onSelectLayer(layer.key);
                                setSurveyTopLayerKey(entry.surveySlug, layer.key);
                              }
                            }}
                          />
                          {showMarkerIcon ? (
                            <span className="slp-layer-marker-icon" title={markerType}>
                              {renderMarkerType(markerType, markerColor)}
                            </span>
                          ) : layer.checked && layer.color && !isWmtsLayer && (
                            <span
                              className="slp-layer-swatch"
                              style={{ backgroundColor: layer.color }}
                              title={layer.color}
                            />
                          )}
                          <span
                            className={`slp-layer-name${layer.checked ? ' slp-layer-name--active' : ''}${layer.key === topLayerKey ? ' slp-layer-name--top' : ''}${isWfsSelectionLocked ? ' slp-layer-name--disabled-playback' : ''}`}
                            onClick={() => {
                              if (isRowDisabled) return;

                              if (!layer.checked) {
                                if (isLayerLoading) return;
                                onToggleLayer(entry.surveySlug, layer.key, true);
                                onSelectLayer(layer.key);
                                setSurveyTopLayerKey(entry.surveySlug, layer.key);
                                return;
                              }

                              onSelectLayer(layer.key);
                              setSurveyTopLayerKey(entry.surveySlug, layer.key);
                            }}
                            title={isWfsSelectionLocked
                              ? 'Disabled while playback is open'
                              : !layer.checked && isLayerLoading
                                ? 'Loading layer…'
                                : layer.checked
                                ? (layer.key === topLayerKey ? 'Top-most layer' : 'Click to bring layer to top')
                                : 'Click to turn layer on'}
                          >
                            {layer.name}
                          </span>
                          {isLayerLoading && (
                            <FiRefreshCw className="slp-loading-icon rotating" aria-label="Loading layer" />
                          )}
                          {!!layer.error && !isLayerLoading && (
                            <span className="slp-error-icon" title={layer.error} aria-label={layer.error}>
                              <BsExclamationLg color="red" size={16} aria-hidden />
                            </span>
                          )}
                          <span className="slp-layer-type">{layer.type.toUpperCase()}</span>
                        </div>
                          );
                        })()
                      ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Draggable>
    </Modal>

    {ReactDOM.createPortal(
      <img
        ref={blankDragImageRef}
        src={TRANSPARENT_PIXEL_GIF}
        alt=""
        className="slp-drop-pin-blank-drag-image"
        width={1}
        height={1}
        aria-hidden
        draggable={false}
      />,
      document.body,
    )}

    {ReactDOM.createPortal(
      <div
        ref={dropPinCursorElRef}
        className="slp-drop-pin-drag-cursor"
        aria-hidden
      >
        <span className="slp-drop-pin-drag-cursor__pulse" />
        <img
          ref={dropPinCursorIconRef}
          src={dropPinIconUrl}
          alt=""
          className="slp-drop-pin-drag-cursor__icon"
          width={DROP_PIN_CURSOR_SIZE_PX}
          height={DROP_PIN_CURSOR_SIZE_PX}
          draggable={false}
        />
      </div>,
      document.body,
    )}

    {ReactDOM.createPortal(
      <Menu id={MENU_ID} style={{ '--contexify-zIndex': String((zIndex ?? 0) + 1) } as React.CSSProperties}>
        <Item onClick={handleZoomToExtent}>
          <MdFullscreenExit style={{ marginRight: 6 }} />
          Zoom to extent
        </Item>
        <Item disabled={({ props }) => !isWfsProps(props)} onClick={handleOpenSymbology}>
          <MdColorLens style={{ marginRight: 6 }} />
          Symbology
        </Item>
        <Item disabled={({ props }) => !isWfsProps(props)} onClick={handleGoToStart}>
          <MdReplay style={{ marginRight: 6 }} />
          Go to start
        </Item>
      </Menu>,
      document.body
    )}

    {symbologyOpen && (
      <Symbology
        isOpen={symbologyOpen}
        closeModal={() => setSymbologyOpen(false)}
        itemKey={symbologyData.key}
        category=""
        title={symbologyData.name}
        setLayerStyle={setLayerStyle}
        currentColor={styles[symbologyData.key]?.currentColor}
        currentWidth={styles[symbologyData.key]?.currentWidth}
        currentSize={styles[symbologyData.key]?.currentSize}
        currentLineStyle={styles[symbologyData.key]?.currentLineStyle}
        currentMarkerStyle={styles[symbologyData.key]?.currentMarkerStyle}
        currentAngle={styles[symbologyData.key]?.currentAngle}
        layerType={styles[symbologyData.key]?.type}
        zIndex={(zIndex ?? 0) + 1}
      />
    )}
    </>
  );
};

export default SurveyLayerPanel;
